import { apiClient } from '@/lib/apiClient';

// ── Types matching actual API responses ──────────────────────────

export interface TimelineEvent {
  id: number;
  user_id: number;
  event_type: 'login' | 'logout' | 'call' | 'email' | 'text' | 'meeting' | 'note' | 'lead_update' | 'lead_created' | 'lead_updated' | 'lead_viewed' | 'lead_assigned' | 'proposal' | 'task' | 'api_request' | 'presence_online' | 'presence_offline' | 'other';
  description: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface TimelineResponse {
  events: TimelineEvent[];
  total: number;
  page: number;
  per_page: number;
}

export interface DailyBreakdown {
  date: string;
  calls: number;
  emails: number;
  texts: number;
  meetings: number;
  total_actions: number;
}

export interface SummaryResponse {
  success: boolean;
  user_id: number;
  period: { start: string; end: string };
  login_count: number;
  last_login_at: string | null;
  calls: { attempted: number; completed: number; total_duration_minutes: number };
  emails: { sent: number };
  texts: { sent: number };
  leads: { created: number; updated: number; viewed: number; assigned: number };
  total_api_actions: number;
  total_activities: number;
  sessions: {
    total: number;
    total_minutes: number;
    avg_session_minutes: number;
    first_activity: string;
    last_activity: string;
  };
  active_minutes_estimate: number;
  daily_breakdown: DailyBreakdown[];
}

export interface HeatmapCell {
  day_of_week: number;
  hour: number;
  activity_count: number;
  calls: number;
  emails: number;
  other: number;
  api_requests: number;
  logins: number;
  other_events: number;
  total: number;
}

export interface HeatmapGridCell {
  hour?: number;
  total: number;
  calls: number;
  emails: number;
  api_requests: number;
  logins: number;
  other: number;
  other_events?: number;
}

export interface HeatmapDailyRow {
  date: string;
  day: number;
  day_of_week?: number;
  day_name: string;
  day_total: number;
  cells: HeatmapGridCell[];
}

export interface HeatmapTotals {
  activity_count: number;
  calls: number;
  emails: number;
  other: number;
  api_requests: number;
  logins: number;
  other_events: number;
}

export interface HeatmapResponse {
  success: boolean;
  user_id: number;
  period: { start: string; end: string };
  heatmap: HeatmapCell[];
  hours?: string[];
  grid?: HeatmapGridCell[][];
  daily_grid?: HeatmapDailyRow[];
  totals?: HeatmapTotals;
}

const DEFAULT_HEATMAP_HOURS = Array.from({ length: 25 }, (_, hour) => `${hour}:00`);

const deriveHeatmapCellTotal = (cell?: Partial<HeatmapGridCell>) => {
  if (!cell) return 0;

  return (
    (cell.calls ?? 0) +
    (cell.emails ?? 0) +
    (cell.other ?? 0) +
    (cell.api_requests ?? 0) +
    (cell.logins ?? 0) +
    (cell.other_events ?? 0)
  );
};

const normalizeHeatmapGridCell = (cell: Partial<HeatmapGridCell> | undefined, hour: number): HeatmapGridCell => ({
  hour,
  total: cell?.total ?? deriveHeatmapCellTotal(cell),
  calls: cell?.calls ?? 0,
  emails: cell?.emails ?? 0,
  api_requests: cell?.api_requests ?? 0,
  logins: cell?.logins ?? 0,
  other: cell?.other ?? 0,
  other_events: cell?.other_events ?? 0,
});

const normalizeHeatmapDailyRow = (row: HeatmapDailyRow): HeatmapDailyRow => {
  const cells = Array.from({ length: 24 }, (_, hour) => {
    const indexedCell = row.cells?.[hour];
    const matchedCell = row.cells?.find(cell => cell.hour === hour);

    return normalizeHeatmapGridCell(indexedCell ?? matchedCell, hour);
  });

  return {
    ...row,
    cells,
    day_total: row.day_total ?? cells.reduce((sum, cell) => sum + cell.total, 0),
  };
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Build a complete daily_grid covering every date in the period, filling gaps with empty rows */
const buildCompleteDailyGrid = (response: HeatmapResponse): HeatmapDailyRow[] | undefined => {
  const { period, grid, daily_grid } = response;
  if (!period?.start || !period?.end) return daily_grid?.map(normalizeHeatmapDailyRow);

  // Build a lookup from existing daily_grid rows
  const existingRows = new Map<string, HeatmapDailyRow>();
  if (daily_grid) {
    daily_grid.forEach(row => existingRows.set(row.date, row));
  }

  // Generate all dates in the period
  const allDates: string[] = [];
  const start = new Date(period.start + 'T00:00:00');
  const end = new Date(period.end + 'T00:00:00');
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    allDates.push(d.toISOString().slice(0, 10));
  }

  // If we have daily_grid, fill missing dates
  if (daily_grid && daily_grid.length > 0) {
    return allDates.map(dateStr => {
      const existing = existingRows.get(dateStr);
      if (existing) return normalizeHeatmapDailyRow(existing);
      const dt = new Date(dateStr + 'T00:00:00');
      const emptyCells = Array.from({ length: 24 }, (_, h) => normalizeHeatmapGridCell(undefined, h));
      return { date: dateStr, day: dt.getDate(), day_name: DAY_NAMES[dt.getDay()], day_total: 0, cells: emptyCells };
    });
  }

  // If we only have grid (array of arrays), map rows to dates
  if (grid && grid.length > 0) {
    return allDates.map((dateStr, idx) => {
      const dt = new Date(dateStr + 'T00:00:00');
      const rawRow = grid[idx];
      const cells = rawRow
        ? Array.from({ length: 24 }, (_, h) => normalizeHeatmapGridCell(rawRow[h], h))
        : Array.from({ length: 24 }, (_, h) => normalizeHeatmapGridCell(undefined, h));
      const day_total = cells.reduce((s, c) => s + c.total, 0);
      return { date: dateStr, day: dt.getDate(), day_name: DAY_NAMES[dt.getDay()], day_total, cells };
    });
  }

  return daily_grid?.map(normalizeHeatmapDailyRow);
};

const normalizeHeatmapResponse = (response: HeatmapResponse): HeatmapResponse => ({
  ...response,
  hours: response.hours?.length ? response.hours : DEFAULT_HEATMAP_HOURS,
  grid: response.grid?.map((row = []) => Array.from({ length: 24 }, (_, hour) => normalizeHeatmapGridCell(row[hour], hour))),
  daily_grid: buildCompleteDailyGrid(response),
});

export interface PresenceOnlineUser {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  connected_at: string;
  last_seen_at: string;
  connection_count: number;
}

export interface PresenceResponse {
  online_users: PresenceOnlineUser[];
  online_count: number;
  total_users: number;
}

export interface TeamSummaryResponse {
  team: SummaryResponse[];
  period: { start: string; end: string };
}

export interface PerformanceResponse {
  success: boolean;
  user_id: number;
  period: { start: string; end: string };
  total_actions: number;
  work_days: number;
  actions_per_day: number;
  calls_per_day: number;
  streak_days: number;
  calls: {
    attempted: number;
    completed: number;
    success_rate: number;
    total_duration_minutes: number;
    talk_minutes: number;
    avg_duration_seconds: number;
  };
  work_pattern: {
    most_active_day: string;
    most_active_hour: number;
    avg_start_hour: number;
    avg_end_hour: number;
  };
}

// ── API Service ────────────────────────────────────────────────

class UserActivityApi {
  private buildQuery(params: Record<string, string | number | undefined>): string {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null) query.set(key, String(val));
    });
    const qs = query.toString();
    return qs ? `?${qs}` : '';
  }

  async getTimeline(userId: number, params?: { page?: number; per_page?: number; event_type?: string; start_date?: string; end_date?: string }): Promise<TimelineResponse> {
    const qs = this.buildQuery({ user_id: userId, ...params });
    const raw = await apiClient.get<any>(`/user-activity/timeline${qs}`);
    return {
      events: raw.activities ?? raw.events ?? [],
      total: raw.total ?? 0,
      page: raw.page ?? 1,
      per_page: raw.per_page ?? raw.limit ?? 50,
    };
  }

  async getUserSummary(userId: number, startDate?: string, endDate?: string): Promise<SummaryResponse> {
    const qs = this.buildQuery({ user_id: userId, start_date: startDate, end_date: endDate });
    return apiClient.get<SummaryResponse>(`/user-activity/summary${qs}`);
  }

  async getTeamSummary(startDate?: string, endDate?: string): Promise<TeamSummaryResponse> {
    const qs = this.buildQuery({ start_date: startDate, end_date: endDate });
    return apiClient.get<TeamSummaryResponse>(`/user-activity/team-summary${qs}`);
  }

  async getPresence(): Promise<PresenceResponse> {
    return apiClient.get<PresenceResponse>('/user-activity/presence');
  }

  async getHeatmap(userId: number, startDate?: string, endDate?: string): Promise<HeatmapResponse> {
    const qs = this.buildQuery({ user_id: userId, start_date: startDate, end_date: endDate });
    const response = await apiClient.get<HeatmapResponse>(`/user-activity/heatmap${qs}`);
    return normalizeHeatmapResponse(response);
  }

  async getPerformance(userId: number, startDate?: string, endDate?: string): Promise<PerformanceResponse> {
    const qs = this.buildQuery({ user_id: userId, start_date: startDate, end_date: endDate });
    return apiClient.get<PerformanceResponse>(`/user-activity/performance${qs}`);
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/user-activity/logout', {});
    } catch (e) {
      console.warn('Failed to record logout activity:', e);
    }
  }
}

export const userActivityApi = new UserActivityApi();
